import { useCallback, useState } from "react";
import type { PermissionState } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import type { StreamChat } from "stream-chat";
import {
  getChatPushPermission,
  registerChatPushDevice,
  removeChatPushDevice,
  requestChatPushPermission,
  setChatPushPreference,
} from "Infrastructure/Capacitor/chatPushDevice";

type ToggleResult = {
  permission: PermissionState;
  isEnabled: boolean;
};

/**
 * Turns chat push on/off for this device. On = prompt if the OS still allows it, register with
 * Stream under the connected persona, then store the opt-in. Off = drop the device from Stream,
 * then store the opt-out so later launches stay silent. The preference is written only after the
 * Stream call succeeded, so a failure leaves it untouched (and Sentry informed) and the returned
 * state matches what the next Settings visit will read back.
 */
export const useChatPushDeviceToggle = (client: StreamChat | undefined) => {
  const [isBusy, setIsBusy] = useState(false);

  const toggle = useCallback(
    async (enable: boolean): Promise<ToggleResult> => {
      if (!client) {
        return { permission: await getChatPushPermission(), isEnabled: false };
      }
      setIsBusy(true);
      try {
        if (enable) {
          const permission = await requestChatPushPermission();
          if (permission !== "granted") {
            return { permission, isEnabled: false };
          }
          await registerChatPushDevice(client);
          setChatPushPreference("on");
          return { permission, isEnabled: true };
        }

        await removeChatPushDevice(client);
        setChatPushPreference("off");
        return { permission: await getChatPushPermission(), isEnabled: false };
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: "chatPush" } });
        return {
          permission: await getChatPushPermission(),
          isEnabled: !enable,
        };
      } finally {
        setIsBusy(false);
      }
    },
    [client],
  );

  return { toggle, isBusy };
};
